import { apiClient } from '@/utils/api'

/** Group type: what kind of organization this group represents */
export type GroupType = 'club' | 'individual' | 'organisation' | 'community' | 'collaboration'

/** Bank details for displaying payment information (not for processing) */
export interface BankDetails {
  accountName?: string
  sortCode?: string      // UK sort code (e.g., "12-34-56")
  accountNumber?: string // Account number
  iban?: string          // International bank account number
  bic?: string           // BIC/SWIFT code
  reference?: string     // Suggested payment reference
  notes?: string         // Additional notes (e.g., "Include your name as reference")
}

export interface Group {
  id: string
  name: string
  icon: string
  /** Type of group (default: group) */
  type?: GroupType
  showOrganisers?: boolean
  isPublic?: boolean
  isFeatured?: boolean
  /** Whether public visitors can submit join requests (default: true) */
  allowJoinRequests?: boolean
  /** Bank details for displaying on season signups */
  bankDetails?: BankDetails
  memberCount: number
  isAdmin: boolean
  isSuperAdmin?: boolean
  isParticipant: boolean
  myRole?: 'owner' | 'admin' | 'member' | null
  myBracket?: string | null
  currentSeason?: string
  seasonStart?: string
  seasonEnd?: string
  pastSeasons?: number
  brackets?: string[]
  status: 'active' | 'upcoming' | 'new' | 'finished'
}

export type GroupMember = {
  userId: string
  displayName: string
  avatar?: string
  role: 'owner' | 'admin' | 'member'
  joinedAt?: string
  isOrganiser?: boolean
  /** ELO rating within this group (default: 1200) */
  elo?: number
}

export type GroupOrganiser = {
  userId: string
  displayName: string
  avatar?: string
  role: 'owner' | 'admin' | 'member'
}

export type GroupInvite = {
  inviteId: string
  groupId: string
  token: string
  email?: string
  role: 'member' | 'admin'
  status: 'pending' | 'accepted' | 'revoked' | 'expired'
  createdBy: string
  createdAt: string
  expiresAt?: string
  acceptedBy?: string
  acceptedAt?: string
  revokedAt?: string
  acceptPath?: string
}

interface GroupsResponse {
  data: Group[]
  meta: {
    total: number
  }
}

interface GroupResponse {
  data: Group
}

interface GroupMembersResponse {
  data: GroupMember[]
  meta: {
    total: number
  }
}

interface GroupInvitesResponse {
  data: GroupInvite[]
  meta: {
    total: number
  }
}

interface GroupOrganisersResponse {
  data: GroupOrganiser[]
  meta: {
    total: number
  }
}

type ListOptions = {
  /**
   * Bypass the short-lived in-memory cache (still dedupes concurrent calls).
   * Useful after mutating operations (create/update/delete).
   */
  force?: boolean
}

let groupsListInflight: Promise<Group[]> | null = null
let groupsListCache: { data: Group[]; ts: number } | null = null
const GROUPS_LIST_CACHE_TTL_MS = 5_000

export const groupService = {
  async list(options: ListOptions = {}): Promise<Group[]> {
    const { force = false } = options

    const now = Date.now()
    if (!force && groupsListCache && now - groupsListCache.ts < GROUPS_LIST_CACHE_TTL_MS) {
      return groupsListCache.data
    }

    if (groupsListInflight) {
      return groupsListInflight
    }

    groupsListInflight = (async () => {
      try {
        const response = await apiClient.authGet<GroupsResponse>('/groups')
        groupsListCache = { data: response.data, ts: Date.now() }
        return response.data
      } finally {
        groupsListInflight = null
      }
    })()

    return groupsListInflight
  },

  async get(id: string): Promise<Group> {
    const response = await apiClient.authGet<GroupResponse>(`/groups/${id}`)
    return response.data
  },

  async create(data: Partial<Group>): Promise<Group> {
    const response = await apiClient.authPost<GroupResponse>('/groups', data)
    return response.data
  },

  async update(id: string, data: Partial<Group>): Promise<Group> {
    const response = await apiClient.authPut<GroupResponse>(`/groups/${id}`, data)
    return response.data
  },

  async delete(id: string): Promise<void> {
    await apiClient.authDelete(`/groups/${id}`)
  },

  async leave(id: string): Promise<void> {
    await apiClient.authPost(`/groups/${id}/leave`, {})
  },

  async listMembers(id: string): Promise<GroupMember[]> {
    const response = await apiClient.authGet<GroupMembersResponse>(`/groups/${id}/members`)
    return response.data
  },

  async removeMember(groupId: string, userId: string): Promise<void> {
    await apiClient.authDelete(`/groups/${groupId}/members/${userId}`)
  },

  async updateMember(
    groupId: string,
    userId: string,
    data: { isOrganiser?: boolean; role?: 'member' | 'admin' }
  ): Promise<GroupMember> {
    const response = await apiClient.authPatch<{ data: GroupMember }>(`/groups/${groupId}/members/${userId}`, data)
    return response.data
  },

  async listOrganisers(groupId: string): Promise<GroupOrganiser[]> {
    const response = await apiClient.authGet<GroupOrganisersResponse>(`/groups/${groupId}/organisers`)
    return response.data
  },

  async listInvites(id: string): Promise<GroupInvite[]> {
    const response = await apiClient.authGet<GroupInvitesResponse>(`/groups/${id}/invites`)
    return response.data
  },

  async createInvite(
    groupId: string,
    data: { email?: string; role?: 'member' | 'admin'; expiresInDays?: number }
  ): Promise<GroupInvite> {
    const response = await apiClient.authPost<{ data: GroupInvite }>(`/groups/${groupId}/invites`, data)
    return response.data
  },

  async revokeInvite(groupId: string, inviteId: string): Promise<void> {
    await apiClient.authDelete(`/groups/${groupId}/invites/${inviteId}`)
  },

  async acceptInvite(token: string): Promise<{ ok: boolean; groupId?: string; alreadyMember?: boolean }> {
    const response = await apiClient.authPost<{ data: { ok: boolean; groupId?: string; alreadyMember?: boolean } }>(
      `/invites/${token}/accept`,
      {}
    )
    return response.data
  },

  async getInvite(token: string): Promise<GroupInvite> {
    const response = await apiClient.authGet<{ data: GroupInvite }>(`/invites/${token}`)
    return response.data
  },

  /**
   * Get a public group without authentication.
   * Only works for groups with isPublic: true
   */
  async getPublic(id: string): Promise<Group> {
    const response = await apiClient.get<GroupResponse>(`/groups/${id}/public`)
    return response.data
  },

  /**
   * List featured groups (no authentication required).
   */
  async listFeatured(): Promise<Group[]> {
    const response = await apiClient.get<GroupsResponse>('/groups/featured')
    return response.data
  },

  /**
   * Get organisers for a public group without authentication.
   */
  async listOrganisersPublic(groupId: string): Promise<GroupOrganiser[]> {
    const response = await apiClient.get<GroupOrganisersResponse>(`/groups/${groupId}/organisers/public`)
    return response.data
  },
}

